// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

export const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    return {
        contactUsSettings: getSettingsCollection(state).contactUsSettings,
        isAuthenticated: xIsAuthenticated(state),
        billTo: getBillToState(state, session.billToId).value,
        shipTo: getBillToState(state, session.shipToId).value,
        account: getCurrentAccountState(state).value,
    };
};

const mapDispatchToProps = {
    loadCurrentAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ContactUsInitializer: FC<Props> = ({ isAuthenticated, contactUsSettings, loadCurrentAccount, billTo, account, shipTo }) => {
    const isEditing = useShellContext().isEditing;
    const billToOrShipTo = contactUsSettings.populateAddressFieldsFromCustomer;

    useEffect(() => {
        !account && loadCurrentAccount();
    }, []);

    const populateFieldValue = (fieldName: string, value: string) => {
        const element = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement
        if(!element) {
            return;
        }
        element.value = value;
    }

    function populateAddressFields(address: any) {
        if (!address) {
            return;
        }

        populateFieldValue(contactUsSettings.streetFieldName, address.address1);
        populateFieldValue(contactUsSettings.cityFieldName, address.city);
        populateFieldValue(contactUsSettings.stateFieldName, address.state?.abbreviation ?? "None");
        populateFieldValue(contactUsSettings.zipFieldName, address.postalCode);
        const countrySelector = document.querySelector(`[name="${contactUsSettings.countryFieldName}"]`) as HTMLSelectElement
        if(countrySelector) {
            const countryOptionToSelect = Array.from(
                countrySelector.options,
            ).find((option: HTMLOptionElement) => option.value.includes(address.country?.abbreviation));
            if (countryOptionToSelect) {
                countryOptionToSelect.selected = true;
            }
        }
    }

    useEffect(() => {
        if (isAuthenticated && !isEditing) {
            // Populate Account Information Values
            if (account) {
                populateFieldValue(contactUsSettings.firstNameFieldName, (account as any).firstName);
                populateFieldValue(contactUsSettings.lastNameFieldName, (account as any).lastName);
                populateFieldValue(contactUsSettings.emailFieldName, (account as any).email);
                populateFieldValue(contactUsSettings.phoneFieldName, (account as any).properties.phone);
            }

            // Populate Address Information Values
            if (billToOrShipTo === "BillTo") {
                populateAddressFields(billTo);
            } else if (billToOrShipTo === "ShipTo") {
                populateAddressFields(shipTo);
            }
        }
    }, [isAuthenticated, contactUsSettings, isEditing, billTo, shipTo, account]);

    return null;
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContactUsInitializer),
    definition: {
        group: "Contact Us",
    },
};
export default widgetModule;
